/**
 * Folders, groups, and "who can reach this" (044).
 *
 * Kept apart from api/forms.ts because it is a different question about the
 * same objects: forms.ts is what a form IS, this is who may reach it. The
 * separation matters most next to `FormAccess` in forms.ts, which is the other
 * "access" — who out in the world may fill a form in.
 */
import { apiFetch } from './client';

export type Level = 'view' | 'edit' | 'manage';
export const LEVEL_LABEL: Record<Level, string> = { view: 'View', edit: 'Edit', manage: 'Manage' };

/** What each level lets someone do, as the share dialog's select says it. */
export const LEVEL_BLURB: Record<Level, string> = {
  view: 'See the form and read its responses.',
  edit: 'Build it, change it, and work its responses.',
  manage: 'Everything, plus delete it and choose who else can reach it.',
};

export type FolderBase = 'everyone' | 'restricted';
/** A form may also defer to its folder; a folder has no folder to defer to. */
export type FormBase = 'inherit' | FolderBase;

export interface FolderSummary {
  id: number;
  name: string;
  sort_order: number;
  base_access: FolderBase;
  base_level: 'view' | 'edit';
  /** forms in it that the CALLER can reach — never a number they can't verify */
  form_count: number;
}

/** One person or group on a form's Access cell. */
export interface SharedWith {
  type: 'user' | 'group';
  id: number;
  name: string;
  active: boolean;
  level: Level;
  /** the folder it came from, or null for a grant on the form itself */
  viaFolder: string | null;
}

export interface FormSharing {
  base: FolderBase;
  baseLevel: 'view' | 'edit';
  /** true when the form defers to its folder rather than saying for itself */
  inherits: boolean;
  /** the caller's own level; 'manage' for anyone who bypasses grants */
  myLevel: Level | null;
  people: SharedWith[];
}

// ---- folders ----
export const listFoldersApi = () =>
  apiFetch<{ success: boolean; folders: FolderSummary[] }>('/admin/folders');

export const createFolderApi = (name: string, baseAccess: FolderBase, baseLevel: 'view' | 'edit') =>
  apiFetch<{ success: boolean; folder: FolderSummary }>('/admin/folders', {
    method: 'POST', body: JSON.stringify({ name, baseAccess, baseLevel }),
  });

export const renameFolderApi = (id: number, name: string) =>
  apiFetch<{ success: boolean; folder: FolderSummary }>(`/admin/folders/${id}`, {
    method: 'PATCH', body: JSON.stringify({ name }),
  });

export const folderDeletionApi = (id: number) =>
  apiFetch<{ success: boolean; formCount: number; losing: { type: string; name: string; level: Level }[] }>(
    `/admin/folders/${id}/deletion`
  );

export const deleteFolderApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/folders/${id}`, { method: 'DELETE' });

/** Move one or many. `folderId: null` is Unfiled, which is a state not a row. */
export const moveFormsApi = (formIds: number[], folderId: number | null) =>
  apiFetch<{ success: boolean; moved: number }>('/admin/folders/move', {
    method: 'POST', body: JSON.stringify({ formIds, folderId }),
  });

// ---- the share dialog ----
export interface ShareGrant {
  subjectType: 'user' | 'group';
  subjectId: number;
  name: string;
  email: string | null;
  active: boolean;
  memberCount: number | null;
  level: Level;
  viaFolder: { id: number; name: string } | null;
  grantedBy: string | null;
  grantedAt: string;
}

export interface ShareState {
  subject: { kind: 'form' | 'folder'; id: number; name: string };
  base: FolderBase;
  baseLevel: 'view' | 'edit';
  inherits: boolean;
  folder: { id: number; name: string } | null;
  direct: ShareGrant[];
  inherited: ShareGrant[];
  candidates: {
    users: { id: number; name: string; email: string; active: boolean; role: string }[];
    groups: { id: number; name: string; memberCount: number }[];
  };
}

export interface SharePreview {
  losing: { id: number; name: string }[];
  losingCount: number;
  formCount: number;
  unaffected: { id: number; name: string; base_access: string }[];
  lastManage: boolean;
}

export interface ShareWrite {
  /** Omitted when the radios were never touched, so an inheriting form
   *  is not silently frozen at whatever it resolved to when drawn. */
  base?: FormBase;
  baseLevel: 'view' | 'edit';
  grants: { subjectType: 'user' | 'group'; subjectId: number; level: Level }[];
}

const shareRoot = (kind: 'form' | 'folder', id: number) =>
  kind === 'form' ? `/admin/forms/${id}` : `/admin/folders/${id}`;

export const getShareApi = (kind: 'form' | 'folder', id: number) =>
  apiFetch<{ success: boolean; share: ShareState }>(`${shareRoot(kind, id)}/share`);

export const previewShareApi = (kind: 'form' | 'folder', id: number, body: ShareWrite) =>
  apiFetch<{ success: boolean; preview: SharePreview }>(`${shareRoot(kind, id)}/share/preview`, {
    method: 'POST', body: JSON.stringify(body),
  });

export const saveShareApi = (kind: 'form' | 'folder', id: number, body: ShareWrite) =>
  apiFetch<{ success: boolean; share: ShareState }>(`${shareRoot(kind, id)}/share`, {
    method: 'PUT', body: JSON.stringify(body),
  });

// ---- groups ----
export interface GroupMember {
  id: number; email: string; name: string | null; active: boolean; role: string;
}

export interface GroupSummary {
  id: number;
  name: string;
  created_at: string;
  member_count: number;
  members: GroupMember[];
  folder_count: number;
  form_count: number;
}

export interface AccountUserLite {
  id: number; name: string; email: string; active: boolean; role: string;
}

export const listGroupsApi = () =>
  apiFetch<{ success: boolean; groups: GroupSummary[]; users: AccountUserLite[] }>('/admin/groups');

export const createGroupApi = (name: string, userIds: number[]) =>
  apiFetch<{ success: boolean; group: GroupSummary }>('/admin/groups', {
    method: 'POST', body: JSON.stringify({ name, userIds }),
  });

export const updateGroupApi = (id: number, body: { name?: string; userIds?: number[] }) =>
  apiFetch<{ success: boolean; group: GroupSummary }>(`/admin/groups/${id}`, {
    method: 'PATCH', body: JSON.stringify(body),
  });

export const groupDeletionApi = (id: number) =>
  apiFetch<{ success: boolean; memberCount: number; folderCount: number; formCount: number }>(
    `/admin/groups/${id}/deletion`
  );

export const deleteGroupApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/groups/${id}`, { method: 'DELETE' });

// ---- the reach panel ----
export interface ReachLine {
  id: number;
  name: string;
  folder?: string | null;
  level: Level;
  /** how they got it: a group, a direct grant with who and when, or base access */
  route: string;
}

export interface UserReach {
  user: { id: number; name: string; email: string; role: string };
  everything: boolean;
  folders: ReachLine[];
  forms: ReachLine[];
  unreachable: { id: number; name: string }[];
}

export const userReachApi = (id: number) =>
  apiFetch<{ success: boolean } & UserReach>(`/admin/users/${id}/reach`);

// ---- where new forms land (Q3) ----
export const setNewFormAccessApi = (newFormAccess: 'everyone' | 'creator') =>
  apiFetch<{ success: boolean; newFormAccess: 'everyone' | 'creator' }>('/admin/settings/sso/new-form-access', {
    method: 'PATCH', body: JSON.stringify({ newFormAccess }),
  });

// ---------------------------------------------------------------------------
// Avatars
// ---------------------------------------------------------------------------

/**
 * Two initials for a person or a group.
 *
 * A group gets its name's initials on the same ramp as a person's, so a stack
 * reads as "who can reach this" rather than "some people and also some other
 * kind of thing" — the artboard draws `FT` for Field techs beside `SR` for
 * Sam Reed and the eye does not have to do any work to combine them.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/[\s@._-]+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * A stable colour from the seven semantic pairs.
 *
 * Never mixes a pair — background and foreground always come from the same
 * intent — and the choice is a hash of the name so the same person is the same
 * colour on every screen and across reloads.
 */
const AVATAR_INTENTS = ['identity', 'info', 'ok', 'warn', 'caution', 'neutral'] as const;

export function avatarIntent(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_INTENTS[hash % AVATAR_INTENTS.length];
}
